import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { camelCase, capitalize, isNil } from 'lodash-es';
import { listViewGridOption } from '@/common/define/grid.define';
import {
  CustomColumn,
  CustomColumnsReadonly,
  GridOption,
  GridSearchBarOption,
  MenuItem,
} from '@/common/utils/types';
import { ExportExcelOption } from '@/common/utils/exportExcelUtils';
import { useInternational } from '@/common/locale';
import { formatNumberToLocale, getJsonParse, roundToDigit } from '@/common/utils/commonUtils';
import { useTargetDetailProvide } from '@/alert/components/alertTargetList/alertTargetList.uses';
import { useSimpleTextTooltip } from '@/common/components/molecules/simpleTextTooltip/simpleTextTooltip.uses';
import { findGridColumnIndex } from '@/common/utils/gridUtils';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { useDashboardPopupEnvStore } from '@/common/stores/dashboard-popup';
import { ALERT_RULE_NAME_ICON } from '@/alert/utils/tagsAndTargets.uses';
import { AlertThresholdInfo } from '@/alert/components/alertGrid/alertGrid.uses';
import { type NotificationMethod } from '@/api-client/alert/model';
import { ValueWindow } from '@/alert/components/alertGrid/alertGrid.types';
import { useLogsKeywordSettingStore } from '@/logs/stores/useKeywordSettingModal';
import { NotificationType } from '@/alert/utils/notification.types';
import {
  highlightLog,
  getAddedKeysHighlightInfo,
  getFilteredKeysHighlightInfo,
} from '@/logs/utils/logMessage.utils';
import { FILE_CHANGE_MAP, getFileNoChangeUnitItems } from '@/alert/utils/define';
import {
  alertLevelSortFn,
  getDisplayNameFromSelectboxItem,
  useAlertFormatter,
  alertTargetSortFn,
} from '@/alert/utils/utils';
import { getEventTypeKeys } from '@/config/views/userAlert/alertRuleDataConditions/alertRuleDataConditionEventServerChange.define';
import dayjs from 'dayjs';
import { getFilterItemsByGridColumns } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { resolveAlertValueViewType } from './alertGrid.utils';
import { openSafeUrl } from '@/common/utils/browser/navigation';

export interface Props {
  gridColumns: CustomColumn[] | CustomColumnsReadonly;
  gridData: any[];
  type?: 'main' | 'detail';
  componentIs?: 'grid' | 'treeGrid';
  checkedRows?: any[];
  searchWord?: string;
  options?: {
    useGridSetting?: boolean;
    useCheckbox?: boolean;
    useExcelFromServer?: boolean;
    useSearchBar?: boolean;
    usePage?: boolean;
    gridOption?: GridOption;
    useDashboardJump?: boolean;
  };
  envKey?: string;
  filterEnvKey?: string;
  exportExcelTitle?: string;
  isTargetClickable?: boolean;
  ruleId?: string;
  timePeriodInfo?: {
    fromTime: string;
    toTime: string;
  };
  initialFilterValues?: {
    logicalOperator: 'AND' | 'OR';
    tokens: Array<{
      key: string;
      operator: string;
      value: string;
    }>;
  };
  useMaxRowsTooltip?: {
    use: boolean;
    max: number;
  };
}
export interface Emit {
  (e: 'update:checkedRows', value: any[]): void;
  (e: 'update:searchWord', value: string): void;
  (e: 'on-click-cell', value: { field: string; value: any; row: any; cellIndex?: number }): void; // 트리그리드는 cellIndex 미제공
  (e: 'openDetail'): void;
  (
    e: 'export-to-excel-from-server',
    option: ExportExcelOption & { columns?: CustomColumn[] | CustomColumnsReadonly },
  ): void;
  (e: 'click-status-button', value: { iconType: NotificationType; row: any }): void;
}

export const PROPS_DEFAULTS = {
  type: 'main',
  componentIs: 'grid',
  checkedRows: () => [],
  searchWord: '',
  options: () => ({
    useGridSetting: true,
    useCheckbox: false,
    useExcelFromServer: false,
    usePage: false,
    useDashboardJump: false,
  }),
  isTargetClickable: true,
} as const;

export const useCriteriaTooltip = () => {
  const criteriaTooltipMouseEvent = ref<MouseEvent | null>(null);
  const { onMouseEventInTarget } = useSimpleTextTooltip(criteriaTooltipMouseEvent);

  const criteriaTooltipText = ref('');

  const onCriteriaMouse = ({ text, e }: { text: string; e: MouseEvent }) => {
    criteriaTooltipText.value = text;
    onMouseEventInTarget(e);
  };

  return {
    criteriaTooltipMouseEvent,
    criteriaTooltipText,
    onCriteriaMouse,
  };
};

const useDashboardJump = (props: Props) => {
  const { t } = useInternational();

  const isVisibleSetupDashBoard = ref(false);
  const openModal = () => {
    isVisibleSetupDashBoard.value = true;
  };

  const customContextMenu: MenuItem = {
    text: t('MESSAGE.GO_TO_DASHBOARD'),
    click: () => {
      openModal();
    },
  };

  const dashboardEnvKey = USER_ENV_KEYS.ALERT_LIST_DETAIL_HISTORY_LIST_DASHBOARD_SETTING_LIST;
  const { fetchDashboardPopupEnvInfoForKey, deleteSetupDashboardDataByEnvKey } =
    useDashboardPopupEnvStore();
  const setupDashboardPopupClose = async () => {
    await fetchDashboardPopupEnvInfoForKey(dashboardEnvKey);
  };

  const timeInfo = ref('');
  const alertId = ref('');
  const { setupDashBoardData } = storeToRefs(useDashboardPopupEnvStore());

  const dashboardPopupList = computed(
    () => setupDashBoardData.value?.get(dashboardEnvKey)?.dashboardList ?? [],
  );
  const dashboardPopupListCnt = computed(() => dashboardPopupList.value.length ?? 0);
  const dashboardName = computed(() => setupDashBoardData.value?.get(dashboardEnvKey)?.name ?? '');

  const router = useRouter();

  const dashboardListRef = ref();
  const showDashboardListTooltip = (e) => {
    if (dashboardPopupListCnt.value > 1) {
      dashboardListRef.value.show(e);
    }
  };

  const moveDashboard = (id: number) => {
    const timePeriodInfo = {
      fromTime:
        props.timePeriodInfo?.fromTime ||
        dayjs(timeInfo.value).subtract(10, 'minute').format('YYYY-MM-DD HH:mm:ss'),
      toTime: props.timePeriodInfo?.toTime || dayjs(timeInfo.value).format('YYYY-MM-DD HH:mm:ss'),
    };
    router.push({
      name: 'dashboard_Dashboard View',
      params: {
        id,
      },
      query: {
        rootMenu: 'dashboard',
        alertId: alertId.value,
        timeInfo: timeInfo.value,
        ...(timePeriodInfo || {}),
      },
    });
  };

  const viewDashboardList = (e, rowObj) => {
    showDashboardListTooltip(e);
    timeInfo.value = rowObj.triggerTime;
    alertId.value = rowObj.resultId;
    if (dashboardPopupListCnt.value === 1) {
      const firstItem = dashboardPopupList.value[0];
      if (firstItem.dashboardId) {
        moveDashboard(firstItem.dashboardId);
      } else if (firstItem.url) {
        openSafeUrl(firstItem.url, { newWindow: firstItem.isNewWindow });
      }
    }
  };

  const dashboardTooltipRef = ref();
  const onShowDashboardTooltip = (e) => {
    if (dashboardPopupListCnt.value === 0) {
      dashboardTooltipRef.value.show(e);
    }
  };
  const onHideDashboardTooltip = (e) => {
    const { relatedTarget } = e;
    if (!relatedTarget?.classList.contains('dashboard-msg-tooltip')) {
      dashboardTooltipRef.value.hide();
    }
  };

  const wheelEventHandler = () => {
    if (dashboardListRef.value) {
      dashboardListRef.value.hide();
    }
  };

  onMounted(() => {
    window.addEventListener('wheel', wheelEventHandler);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('wheel', wheelEventHandler);
    deleteSetupDashboardDataByEnvKey(dashboardEnvKey);
  });
  return {
    customContextMenu,
    isVisibleSetupDashBoard,
    dashboardListRef,
    dashboardEnvKey,
    dashboardName,
    setupDashboardPopupClose,
    viewDashboardList,
    moveDashboard,
    dashboardPopupListCnt,
    dashboardPopupList,
    dashboardTooltipRef,
    onShowDashboardTooltip,
    onHideDashboardTooltip,
  };
};

export const useAlertValue = (props: Props, emit?: Emit) => {
  const { t } = useInternational();
  const { configKeywordList, userKeywordList } = storeToRefs(useLogsKeywordSettingStore());

  const valueModalInfo = ref<ValueWindow>({
    windowType: 'text',
    isShow: false,
    text: '',
    type: 'none',
    eventType: '',
  });

  const getHostAlertValue = (val: string): string => {
    const parsedValue = getJsonParse(val);
    if (!parsedValue || !('event' in parsedValue)) {
      return '';
    }

    const { event, ...rest } = parsedValue;
    const eventType = camelCase(event);

    if (Object.keys(FILE_CHANGE_MAP).includes(event)) {
      switch (eventType) {
        case 'fileSize':
          // [파일 크기] {파일 경로} (Value: {파일 크기 값} {단위})
          return `[${t('WORD.FILE_SIZE')}] ${rest.filePath} (Value: ${rest.fileSize} ${
            rest.filesizeUnit
          })`;
        case 'fileChange':
          // [파일 변경] {파일 이름}
          return `[${t('WORD.FILE_CHANGE')}] ${rest.filePath}`;
        case 'fileNoChange':
          // [파일 변경 없음] {파일 경로} ({임계 조건} 동안 변경 없음)
          return `[${t('WORD.FILE_NO_CHANGE')}] ${rest.filePath} (${t('WORD.NO_CHANGES_PERIOD', {
            value: `${rest.fileNoChangeThreshold} ${getDisplayNameFromSelectboxItem(
              getFileNoChangeUnitItems(),
              rest.fileNoChangeUnit,
            )}`,
          })})`;
        default:
          return '';
      }
    }

    if (eventType === 'serverChange') {
      const {
        value: { type = '', diff = [] },
      } = rest;

      const serverChangeEventType =
        Object.values(getEventTypeKeys()).find((item) => item.key === type)?.eventType ?? '';

      let resultValue = '';

      diff.forEach((item, idx) => {
        if (idx > 0) {
          resultValue += '\n';
        }

        const { name, value, prevValue, ...updatedItem } = item;

        if (value || prevValue) {
          resultValue += `[${serverChangeEventType}] ${name} (Value: ${value}, Prev Value: ${prevValue})\n`;
        } else if (updatedItem) {
          resultValue += `[${serverChangeEventType}] ${name}\n`;
          Object.entries(updatedItem).forEach(([updatedKey, updatedValue = []]) => {
            resultValue += `${capitalize(updatedKey)}: ${
              Array.isArray(updatedValue) ? updatedValue.join(', ') : updatedValue
            }\n`;
          });
        }
      });

      return resultValue;
    }
    return val;
  };

  const getAlertValue = (val: string, rowData: Record<string, any>, statUnit?: string) => {
    if (val == null || typeof val === 'object') {
      return '';
    }

    const eventType = rowData.eventType?.toLowerCase();
    let parsedValue: any = {};

    switch (eventType) {
      case 'kubernetes':
        parsedValue = getJsonParse(val);
        if (parsedValue && 'kind' in parsedValue) {
          const { message, kind, reason } = parsedValue;
          return `${message} (Kind: ${kind[0]}, Reason: ${reason})`;
        }
        return val;
      case 'logging':
        return val;
      case 'host':
        return getHostAlertValue(val);
      case 'database':
        return `${!val || Number.isNaN(+val) ? val : roundToDigit(+val, 3)}`;
      default:
        return `${!val || Number.isNaN(+val) ? val : roundToDigit(+val, 3)} ${
          val && statUnit ? statUnit : ''
        }`;
    }
  };

  const getParsedValue = (val: string) => {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  };
  const getLoggingAlertValue = (val: string, noHighlight = false) => {
    if (!val) {
      return null;
    }
    const data = getParsedValue(val);
    if (typeof data === 'string') {
      return data;
    }

    const { filtered_keys: filteredKeys, message } = data;

    const plainMessage = message ?? '';
    if (noHighlight) {
      return plainMessage;
    }

    let filteredKeysToArray: string[] = [];
    if (Array.isArray(filteredKeys)) {
      filteredKeysToArray = filteredKeys;
    } else if (typeof filteredKeys === 'string') {
      filteredKeysToArray = [filteredKeys];
    }

    const configList = getFilteredKeysHighlightInfo(
      filteredKeysToArray ?? [],
      configKeywordList.value,
    );

    const userList = getAddedKeysHighlightInfo(plainMessage, userKeywordList.value);
    return highlightLog(plainMessage, {
      configKeywordList: configList,
      userKeywordList: userList,
    });
  };

  const onClickLinkedValue = (rowData: Record<string, any>) => {
    const alertValue = getAlertValue(rowData.value, rowData);
    const ruleId = props.ruleId || rowData.alertRuleId;
    if (rowData.eventType?.toLowerCase() === 'logging' && ruleId) {
      valueModalInfo.value = {
        windowType: 'logging',
        isShow: true,
        targetId: rowData.targets[0]?.id,
        ruleId,
        resultId: rowData.resultId,
        fromTime: props.timePeriodInfo?.fromTime,
        toTime: props.timePeriodInfo?.toTime,
      };
    } else {
      valueModalInfo.value = {
        windowType: 'text',
        isShow: true,
        type: 'none',
        text: alertValue,
        eventType: rowData.eventType,
      };
    }

    if (emit) {
      emit('on-click-cell', {
        field: 'value',
        value: alertValue,
        row: rowData,
      });
    }
  };

  return {
    valueModalInfo,
    onClickLinkedValue,
    getAlertValue,
    getLoggingAlertValue,
  };
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { alertName } = useAlertFormatter();

  const {
    customContextMenu,
    isVisibleSetupDashBoard,
    dashboardListRef,
    dashboardEnvKey,
    setupDashboardPopupClose,
    dashboardName,
    viewDashboardList,
    moveDashboard,
    dashboardPopupListCnt,
    dashboardPopupList,
    dashboardTooltipRef,
    onShowDashboardTooltip,
    onHideDashboardTooltip,
  } = useDashboardJump(props);

  const { getAlertValue, getLoggingAlertValue } = useAlertValue(props);

  const checkedRows = computed({
    get: () => props.checkedRows,
    set: (val) => emit('update:checkedRows', val || []),
  });
  const searchWord = computed({
    get: () => props.searchWord,
    set: (val) => emit('update:searchWord', val ?? ''),
  });

  const formatThresholdStr = (
    thresholdInfo: Partial<AlertThresholdInfo> & { statUnit: string },
  ) => {
    const {
      alertName: originalAlertName,
      evaluateValue,
      duringTimeInterval,
      comparison,
      warning,
      critical,
      statUnit,
    } = thresholdInfo || {};

    const threshold = [
      originalAlertName ? alertName(originalAlertName) : null,
      evaluateValue,
      duringTimeInterval ? [t('WORD.DURING_THE'), duringTimeInterval] : [],
      comparison,
      !isNil(warning) ? ['Warning', formatNumberToLocale(warning), statUnit] : [],
      !isNil(critical) ? ['Critical', formatNumberToLocale(critical), statUnit] : [],
    ];

    return threshold
      .flat()
      .filter((val) => !isNil(val))
      .join(' ');
  };

  const exportExcelOption = computed<ExportExcelOption>(() => ({
    title: props.exportExcelTitle ?? t('WORD.ALERT_LIST'),
    rowDataFormatter: {
      alertRuleName: (originalValue: string) =>
        originalValue === ALERT_RULE_NAME_ICON ? '' : originalValue,
      targets: (originalValue: { name: string; id: string }[]) => {
        // TODO: targets 배열 길이 지나치게 길 경우 Excel 비정상 저장
        return originalValue.map((value) => value.name).join(', ');
      },
      targetAlias: (originalValue: { name: string }[] | string[] | null) => {
        if (!originalValue?.length) return '';
        if (typeof originalValue[0] === 'object' && 'name' in originalValue[0]) {
          return (originalValue as { name: string }[])
            .map(({ name }) => name)
            .filter(Boolean)
            .join(', ');
        }
        return (originalValue as string[]).filter(Boolean).join(', ');
      },
      tags: (originalValue: { name: string; id: string }[]) => {
        return originalValue.map((value) => value.name).join(', ');
      },
      threshold: (thresholdInfo: AlertThresholdInfo, { statUnit }: { statUnit: string }) =>
        formatThresholdStr({
          ...(thresholdInfo || {}),
          statUnit,
        }),
      value: (value: string, rowData: Record<string, any> | (string | number | null)[]) => {
        if (rowData instanceof Array) {
          const columns = props.gridColumns ?? [];
          const idx = columns.findIndex((col) => col.field === 'statUnit');
          if (idx < 0 || idx >= rowData.length) return value;

          const unit = rowData[idx];
          if (unit == null) return value;

          return getAlertValue(value, {}, String(unit));
        }

        if (resolveAlertValueViewType(rowData as Record<string, any>) === 'logging') {
          return getLoggingAlertValue(value, true);
        }

        const data = (rowData as Record<string, any>).data ?? rowData;
        const statUnit = (rowData as Record<string, any>).statUnit ?? data.statUnit;

        return getAlertValue(value, data, statUnit);
      },
      notificationStatus: (v: NotificationMethod[]) =>
        (v ?? []).reduce(
          (acc, { methodType, success }) =>
            `${acc ? `${acc}, ` : ''}${capitalize(methodType)}: ${
              success ? 'Succeeded' : 'Failed'
            }`,
          '',
        ),
    },
    exportByServer: props.options?.useExcelFromServer ?? false,
  }));

  const gridOption = computed<GridOption>(() => ({
    ...listViewGridOption,
    useCheckbox: {
      use: props.options?.useCheckbox ?? false,
      mode: 'multi',
      headerCheck: true,
    },
    useGridSetting: {
      use: props.options?.useGridSetting ?? true,
      customContextMenu: props.options?.useDashboardJump === true ? [customContextMenu] : undefined,
    },
    page: {
      use: props.options?.usePage ?? false,
      useClient: false,
    },
    maintainScrollOnUpdateRows: true,
    customAscFunction: {
      alertLevel: alertLevelSortFn,
      tags: alertTargetSortFn,
      targets: alertTargetSortFn,
    },
    ...(props.options?.gridOption || {}),
  }));
  const searchBarOption = computed<GridSearchBarOption>(() => {
    if (props.options?.useSearchBar === false) {
      return { use: false };
    }
    return {
      use: true,
      useMergedToolbar: props.useMaxRowsTooltip?.use,
      filterSearch: {
        use: true,
        storageKey: props.filterEnvKey,
        initialValues: props.initialFilterValues,
        filterItems: getFilterItemsByGridColumns(props.gridColumns),
      },
    };
  });

  const columnIdx = computed(() =>
    findGridColumnIndex({
      fields: props.gridColumns.map(({ field }) => field),
      columns: props.gridColumns,
    }),
  );

  useTargetDetailProvide(emit);

  const { criteriaTooltipMouseEvent, criteriaTooltipText, onCriteriaMouse } = useCriteriaTooltip();

  const onClickCellByField = (field: string, value: any, row: any, cellIndex?: string | number) => {
    emit('on-click-cell', {
      field,
      value,
      row,
      cellIndex: typeof cellIndex === 'string' ? Number(cellIndex) : cellIndex,
    });
  };

  return {
    gridOption,
    searchBarOption,
    checkedRows,
    searchWord,
    exportExcelOption,
    columnIdx,
    t,
    alertName,
    formatThresholdStr,

    criteriaTooltipMouseEvent,
    criteriaTooltipText,
    onCriteriaMouse,

    isVisibleSetupDashBoard,
    dashboardListRef,
    dashboardEnvKey,
    setupDashboardPopupClose,
    viewDashboardList,
    moveDashboard,
    dashboardName,
    dashboardPopupListCnt,
    dashboardPopupList,
    dashboardTooltipRef,
    onShowDashboardTooltip,
    onHideDashboardTooltip,
    onClickCellByField,

    ...useAlertValue(props, emit),
  };
};
